import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { AlertTriangle, CheckCircle, XCircle, Info, TrendingDown, TrendingUp, Minus, Shield, ShieldAlert, ShieldCheck, FileText, Download, RefreshCw } from 'lucide-react';
// This would be fetched from the API in a real implementation
const mockData = {
  riskScore: {
    overall: 65,
    level: 'medium',
    factors: {
      security_posture: 70,
      threat_landscape: 60,
      vulnerability_exposure: 55,
      incident_history: 40,
      data_sensitivity: 80,
      compliance_status: 75,
      user_behavior: 65,
      external_exposure: 70
    }
  },
  postureSummary: {
    overallScore: 72,
    passedControls: 42,
    failedControls: 12,
    warningControls: 8,
    domainScores: {
      identity_access: 85,
      data_protection: 65,
      infrastructure: 75,
      application: 70,
      incident_response: 60,
      compliance: 80,
      endpoint: 65,
      network: 70,
      cloud: 80,
      vendor: 60
    }
  },
  trendSummary: {
    overallTrend: {
      direction: 'improving',
      changePercent: 8
    },
    domainTrends: {
      identity_access: {
        direction: 'improving',
        changePercent: 12
      },
      data_protection: {
        direction: 'improving',
        changePercent: 5
      },
      infrastructure: {
        direction: 'stable',
        changePercent: 2
      },
      application: {
        direction: 'improving',
        changePercent: 8
      },
      incident_response: {
        direction: 'worsening',
        changePercent: 6
      },
      compliance: {
        direction: 'improving',
        changePercent: 10
      },
      endpoint: {
        direction: 'stable',
        changePercent: 3
      },
      network: {
        direction: 'improving',
        changePercent: 7
      },
      cloud: {
        direction: 'improving',
        changePercent: 15
      },
      vendor: {
        direction: 'worsening',
        changePercent: 4
      }
    }
  },
  complianceSummary: [{
    framework: 'iso_27001',
    compliancePercentage: 85,
    compliantCount: 85,
    nonCompliantCount: 15,
    criticalGaps: 2
  }, {
    framework: 'soc_2',
    compliancePercentage: 78,
    compliantCount: 78,
    nonCompliantCount: 22,
    criticalGaps: 3
  }, {
    framework: 'gdpr',
    compliancePercentage: 92,
    compliantCount: 92,
    nonCompliantCount: 8,
    criticalGaps: 0
  }, {
    framework: 'ccpa',
    compliancePercentage: 90,
    compliantCount: 90,
    nonCompliantCount: 10,
    criticalGaps: 1
  }],
  topRisks: [{
    title: 'Multi-Factor Authentication not enforced',
    severity: 'critical',
    domain: 'identity_access'
  }, {
    title: 'Firestore security rules require review',
    severity: 'high',
    domain: 'data_protection'
  }, {
    title: 'Incident response plan outdated',
    severity: 'high',
    domain: 'incident_response'
  }, {
    title: 'Excessive failed login attempts detected',
    severity: 'high',
    domain: 'identity_access'
  }, {
    title: 'Data encryption not enabled for all storage',
    severity: 'high',
    domain: 'data_protection'
  }],
  historicalData: [{
    date: 'Jan',
    riskScore: 78,
    securityScore: 65
  }, {
    date: 'Feb',
    riskScore: 75,
    securityScore: 68
  }, {
    date: 'Mar',
    riskScore: 72,
    securityScore: 70
  }, {
    date: 'Apr',
    riskScore: 70,
    securityScore: 72
  }, {
    date: 'May',
    riskScore: 68,
    securityScore: 75
  }, {
    date: 'Jun',
    riskScore: 65,
    securityScore: 72
  }]
};
// Domain friendly names mapping
const domainNames = {
  identity_access: 'Identity & Access',
  data_protection: 'Data Protection',
  infrastructure: 'Infrastructure',
  application: 'Application',
  incident_response: 'Incident Response',
  compliance: 'Compliance',
  endpoint: 'Endpoint',
  network: 'Network',
  cloud: 'Cloud',
  vendor: 'Vendor'
};
// Framework friendly names mapping
const frameworkNames = {
  iso_27001: 'ISO 27001',
  soc_2: 'SOC 2',
  gdpr: 'GDPR',
  ccpa: 'CCPA',
  hipaa: 'HIPAA',
  pci_dss: 'PCI DSS',
  nist_csf: 'NIST CSF',
  nist_800_53: 'NIST 800-53'
};
// Risk level colors
const riskLevelColors = {
  critical: '#FF3B30',
  high: '#FF9500',
  medium: '#FFCC00',
  low: '#34C759',
  minimal: '#30B0C7'
};
// Severity colors
const severityColors = {
  critical: '#FF3B30',
  high: '#FF9500',
  medium: '#FFCC00',
  low: '#34C759',
  informational: '#007AFF'
};
// Trend direction icons
const TrendIcon = ({
  direction
}) => {
  switch (direction) {
    case 'improving':
      return <TrendingDown className="text-green-500" data-id="element-6113" />;
    case 'worsening':
      return <TrendingUp className="text-red-500" data-id="element-6114" />;
    case 'stable':
    default:
      return <Minus className="text-gray-500" data-id="element-6115" />;
  }
};
const SecurityPostureDashboard = () => {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState('month');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(mockData);
  // This would fetch real data in a production implementation
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // In a real implementation, this would be an API call
        // const response = await fetch(`/api/security/posture?timeframe=${timeframe}`);
        // const data = await response.json();
        // Using mock data for now
        setTimeout(() => {
          setData(mockData);
          setLoading(false);
        }, 500);
      } catch (error) {
        console.error('Error fetching security posture data:', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [timeframe]);
  const handleRefresh = () => {
    setLoading(true);
    // In a real implementation, this would trigger a new assessment
    // fetch('/api/security/posture/assess', { method: 'POST' })
    //   .then(() => fetchData())
    //   .catch(error => console.error('Error triggering assessment:', error));
    // Using mock data for now
    setTimeout(() => {
      setData(mockData);
      setLoading(false);
    }, 1000);
  };
  const handleGenerateReport = reportType => {
    // In a real implementation, this would generate a report
    // window.open(`/api/security/posture/report/${reportType}`, '_blank');
    alert(`Generating ${reportType} report...`);
  };
  // Format domain scores for chart
  const domainScoreData = Object.entries(data.postureSummary.domainScores).map(([domain, score]) => ({
    name: domainNames[domain] || domain,
    score,
    trend: data.trendSummary.domainTrends[domain]?.direction || 'stable',
    changePercent: data.trendSummary.domainTrends[domain]?.changePercent || 0
  }));
  // Format risk factors for chart
  const riskFactorData = Object.entries(data.riskScore.factors).map(([factor, score]) => ({
    name: factor.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
    score
  }));
  // Format compliance data for chart
  const complianceData = data.complianceSummary.map(item => ({
    name: frameworkNames[item.framework] || item.framework,
    compliant: item.compliantCount,
    nonCompliant: item.nonCompliantCount,
    percentage: item.compliancePercentage,
    criticalGaps: item.criticalGaps
  }));
  // Colors for the pie chart
  const COLORS = ['#4CAF50', '#FFC107', '#F44336'];
  // Calculate control status percentages for pie chart
  const totalControls = data.postureSummary.passedControls + data.postureSummary.warningControls + data.postureSummary.failedControls;
  const controlStatusData = [{
    name: 'Passed',
    value: data.postureSummary.passedControls,
    percentage: Math.round(data.postureSummary.passedControls / totalControls * 100)
  }, {
    name: 'Warning',
    value: data.postureSummary.warningControls,
    percentage: Math.round(data.postureSummary.warningControls / totalControls * 100)
  }, {
    name: 'Failed',
    value: data.postureSummary.failedControls,
    percentage: Math.round(data.postureSummary.failedControls / totalControls * 100)
  }];
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-900" data-id="element-6116">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-6117">
        <div className="flex justify-between items-center mb-6" data-id="element-6118">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-6119">
            Security Posture Dashboard
          </h1>
          <div className="flex space-x-4" data-id="element-6120">
            <select className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm py-2 px-3 text-sm" value={timeframe} onChange={e => setTimeframe(e.target.value)} data-id="element-6121">
              <option value="week" data-id="element-6122">Last 7 Days</option>
              <option value="month" data-id="element-6123">Last 30 Days</option>
              <option value="quarter" data-id="element-6124">Last 90 Days</option>
              <option value="year" data-id="element-6125">Last 12 Months</option>
            </select>
            <button onClick={handleRefresh} className="inline-flex items-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700" disabled={loading} data-id="element-6126">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-6127" />
              Refresh
            </button>
            <div className="relative inline-block text-left" data-id="element-6128">
              <div data-id="element-6129">
                <button type="button" className="inline-flex items-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm py-2 px-4 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700" id="report-menu" aria-expanded="true" aria-haspopup="true" onClick={() => {
                const menu = document.getElementById('report-menu-items');
                menu.classList.toggle('hidden');
              }} data-id="element-6130">
                  <FileText className="mr-2 h-4 w-4" data-id="element-6131" />
                  Reports
                  <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-6132">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" data-id="element-6133" />
                  </svg>
                </button>
              </div>
              <div className="hidden origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-10" role="menu" aria-orientation="vertical" aria-labelledby="report-menu" id="report-menu-items" data-id="element-6134">
                <div className="py-1" role="none" data-id="element-6135">
                  <button className="text-gray-700 dark:text-gray-200 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100 dark:hover:bg-gray-700" role="menuitem" onClick={() => handleGenerateReport('executive')} data-id="element-6136">
                    Executive Summary
                  </button>
                  <button className="text-gray-700 dark:text-gray-200 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100 dark:hover:bg-gray-700" role="menuitem" onClick={() => handleGenerateReport('technical')} data-id="element-6137">
                    Technical Report
                  </button>
                  <button className="text-gray-700 dark:text-gray-200 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100 dark:hover:bg-gray-700" role="menuitem" onClick={() => handleGenerateReport('compliance')} data-id="element-6138">
                    Compliance Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Top summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6" data-id="element-6139">
          {/* Risk Score Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-6140">
            <div className="flex justify-between items-start" data-id="element-6141">
              <div data-id="element-6142">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-6143">
                  Overall Risk Score
                </p>
                <div className="flex items-baseline mt-1" data-id="element-6144">
                  <p className={`text-3xl font-semibold ${data.riskScore.level === 'critical' || data.riskScore.level === 'high' ? 'text-red-600 dark:text-red-400' : data.riskScore.level === 'medium' ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`} data-id="element-6145">
                    {data.riskScore.overall}
                  </p>
                  <p className="ml-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-6146">
                    / 100
                  </p>
                </div>
              </div>
              <div className={`rounded-full p-2 ${data.riskScore.level === 'critical' ? 'bg-red-100 dark:bg-red-900' : data.riskScore.level === 'high' ? 'bg-orange-100 dark:bg-orange-900' : data.riskScore.level === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900' : data.riskScore.level === 'low' ? 'bg-green-100 dark:bg-green-900' : 'bg-blue-100 dark:bg-blue-900'}`} data-id="element-6147">
                <ShieldAlert className={`h-6 w-6 ${data.riskScore.level === 'critical' || data.riskScore.level === 'high' ? 'text-red-600 dark:text-red-400' : data.riskScore.level === 'medium' ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`} data-id="element-6148" />
              </div>
            </div>
            <div className="flex items-center mt-4" data-id="element-6149">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${data.riskScore.level === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' : data.riskScore.level === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' : data.riskScore.level === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : data.riskScore.level === 'low' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'}`} data-id="element-6150">
                {data.riskScore.level}
              </span>
              <div className="flex items-center ml-4" data-id="element-6151">
                <TrendIcon direction={data.trendSummary.overallTrend.direction} data-id="element-6152" />
                <span className={`ml-1 text-xs ${data.trendSummary.overallTrend.direction === 'improving' ? 'text-green-600 dark:text-green-400' : data.trendSummary.overallTrend.direction === 'worsening' ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-6153">
                  {data.trendSummary.overallTrend.changePercent}%
                </span>
              </div>
            </div>
          </div>
          {/* Security Posture Score Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-6154">
            <div className="flex justify-between items-start" data-id="element-6155">
              <div data-id="element-6156">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-6157">
                  Security Posture Score
                </p>
                <div className="flex items-baseline mt-1" data-id="element-6158">
                  <p className={`text-3xl font-semibold ${data.postureSummary.overallScore >= 70 ? 'text-green-600 dark:text-green-400' : data.postureSummary.overallScore >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-6159">
                    {data.postureSummary.overallScore}
                  </p>
                  <p className="ml-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-6160">
                    / 100
                  </p>
                </div>
              </div>
              <div className={`rounded-full p-2 ${data.postureSummary.overallScore >= 70 ? 'bg-green-100 dark:bg-green-900' : data.postureSummary.overallScore >= 50 ? 'bg-yellow-100 dark:bg-yellow-900' : 'bg-red-100 dark:bg-red-900'}`} data-id="element-6161">
                <ShieldCheck className={`h-6 w-6 ${data.postureSummary.overallScore >= 70 ? 'text-green-600 dark:text-green-400' : data.postureSummary.overallScore >= 50 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-6162" />
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center" data-id="element-6163">
              <div data-id="element-6164">
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6165">
                  Passed
                </p>
                <p className="text-lg font-semibold text-green-600 dark:text-green-400" data-id="element-6166">
                  {data.postureSummary.passedControls}
                </p>
              </div>
              <div data-id="element-6167">
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6168">
                  Warning
                </p>
                <p className="text-lg font-semibold text-yellow-600 dark:text-yellow-400" data-id="element-6169">
                  {data.postureSummary.warningControls}
                </p>
              </div>
              <div data-id="element-6170">
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-6171">
                  Failed
                </p>
                <p className="text-lg font-semibold text-red-600 dark:text-red-400" data-id="element-6172">
                  {data.postureSummary.failedControls}
                </p>
              </div>
            </div>
          </div>
          {/* Compliance Status Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-6173">
            <div className="flex justify-between items-start" data-id="element-6174">
              <div data-id="element-6175">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-6176">
                  Compliance Status
                </p>
                <div className="flex items-baseline mt-1" data-id="element-6177">
                  <p className="text-3xl font-semibold text-blue-600 dark:text-blue-400" data-id="element-6178">
                    {Math.round(data.complianceSummary.reduce((acc, curr) => acc + curr.compliancePercentage, 0) / data.complianceSummary.length)}
                    %
                  </p>
                </div>
              </div>
              <div className="rounded-full p-2 bg-blue-100 dark:bg-blue-900" data-id="element-6179">
                <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-6180" />
              </div>
            </div>
            <div className="mt-4 text-sm" data-id="element-6181">
              <div className="flex justify-between items-center mb-1" data-id="element-6182">
                <span className="text-gray-500 dark:text-gray-400" data-id="element-6183">
                  {frameworkNames[data.complianceSummary[0].framework]}
                </span>
                <span className="font-medium" data-id="element-6184">
                  {data.complianceSummary[0].compliancePercentage}%
                </span>
              </div>
              <div className="flex justify-between items-center mb-1" data-id="element-6185">
                <span className="text-gray-500 dark:text-gray-400" data-id="element-6186">
                  {frameworkNames[data.complianceSummary[1].framework]}
                </span>
                <span className="font-medium" data-id="element-6187">
                  {data.complianceSummary[1].compliancePercentage}%
                </span>
              </div>
              <div className="flex justify-between items-center" data-id="element-6188">
                <span className="text-gray-500 dark:text-gray-400" data-id="element-6189">
                  Critical Gaps
                </span>
                <span className="font-medium text-red-600 dark:text-red-400" data-id="element-6190">
                  {data.complianceSummary.reduce((acc, curr) => acc + curr.criticalGaps, 0)}
                </span>
              </div>
            </div>
          </div>
          {/* Top Risks Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-6191">
            <div className="flex justify-between items-start mb-4" data-id="element-6192">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-6193">
                Top Risks
              </p>
              <div className="rounded-full p-2 bg-red-100 dark:bg-red-900" data-id="element-6194">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" data-id="element-6195" />
              </div>
            </div>
            <ul className="space-y-2" data-id="element-6196">
              {data.topRisks.slice(0, 3).map((risk, index) => <li key={index} className="flex items-start" data-id="element-6197">
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 mr-2 ${risk.severity === 'critical' ? 'bg-red-600' : risk.severity === 'high' ? 'bg-orange-500' : 'bg-yellow-500'}`} data-id="element-6198"></div>
                  <p className="text-xs text-gray-600 dark:text-gray-300 truncate" data-id="element-6199">
                    {risk.title}
                  </p>
                </li>)}
            </ul>
            <button onClick={() => navigate('/security/risks')} className="mt-4 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300" data-id="element-6200">
              View all risks →
            </button>
          </div>
        </div>
        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-6201">
          {/* Historical Trend Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-6202">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-6203">
              Historical Trend
            </h2>
            <div className="h-80" data-id="element-6204">
              <ResponsiveContainer width="100%" height="100%" data-id="element-6205">
                <LineChart data={data.historicalData} margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-6206">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-6207" />
                  <XAxis dataKey="date" data-id="element-6208" />
                  <YAxis data-id="element-6209" />
                  <Tooltip data-id="element-6210" />
                  <Legend data-id="element-6211" />
                  <Line type="monotone" dataKey="riskScore" name="Risk Score" stroke="#FF9500" activeDot={{
                  r: 8
                }} data-id="element-6212" />
                  <Line type="monotone" dataKey="securityScore" name="Security Score" stroke="#4CAF50" data-id="element-6213" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          {/* Domain Scores Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-6214">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-6215">
              Domain Scores
            </h2>
            <div className="h-80" data-id="element-6216">
              <ResponsiveContainer width="100%" height="100%" data-id="element-6217">
                <BarChart data={domainScoreData} margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} layout="vertical" data-id="element-6218">
                  <CartesianGrid strokeDasharray="3 3" data-id="element-6219" />
                  <XAxis type="number" domain={[0, 100]} data-id="element-6220" />
                  <YAxis dataKey="name" type="category" width={100} tick={{
                  fontSize: 12
                }} data-id="element-6221" />
                  <Tooltip formatter={(value, name, props) => [`${value}%`, 'Score', {
                  trend: props.payload.trend,
                  changePercent: props.payload.changePercent
                }]} content={({
                  active,
                  payload
                }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return <div className="bg-white dark:bg-gray-700 p-2 border border-gray-200 dark:border-gray-600 rounded shadow" data-id="element-6223">
                            <p className="font-medium" data-id="element-6224">{data.name}</p>
                            <p data-id="element-6225">Score: {data.score}%</p>
                            <div className="flex items-center mt-1" data-id="element-6226">
                              <TrendIcon direction={data.trend} data-id="element-6227" />
                              <span className={`ml-1 ${data.trend === 'improving' ? 'text-green-600' : data.trend === 'worsening' ? 'text-red-600' : 'text-gray-500'}`} data-id="element-6228">
                                {data.changePercent}%
                              </span>
                            </div>
                          </div>;
                  }
                  return null;
                }} data-id="element-6222" />
                  <Bar dataKey="score" fill="#4CAF50" radius={[0, 4, 4, 0]} barSize={20} data-id="element-6229" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        {/* Bottom section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-6230">
          {/* Risk Factors */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-6231">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-6232">
              Risk Factors
            </h2>
            <div className="space-y-4" data-id="element-6233">
              {riskFactorData.map((factor, index) => <div key={index} data-id="element-6234">
                  <div className="flex justify-between mb-1" data-id="element-6235">
                    <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-6236">
                      {factor.name}
                    </span>
                    <span className={`text-sm font-medium ${factor.score >= 80 ? 'text-red-600 dark:text-red-400' : factor.score >= 60 ? 'text-orange-600 dark:text-orange-400' : factor.score >= 40 ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`} data-id="element-6237">
                      {factor.score}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2" data-id="element-6238">
                    <div className={`h-2 rounded-full ${factor.score >= 80 ? 'bg-red-600' : factor.score >= 60 ? 'bg-orange-500' : factor.score >= 40 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{
                  width: `${factor.score}%`
                }} data-id="element-6239"></div>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Compliance Status */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-6240">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-6241">
              Compliance Status
            </h2>
            <div className="space-y-4" data-id="element-6242">
              {complianceData.map((item, index) => <div key={index} data-id="element-6243">
                  <div className="flex justify-between mb-1" data-id="element-6244">
                    <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-6245">
                      {item.name}
                    </span>
                    <div className="flex items-center" data-id="element-6246">
                      <span className={`text-sm font-medium ${item.percentage >= 80 ? 'text-green-600 dark:text-green-400' : item.percentage >= 60 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-6247">
                        {item.percentage}%
                      </span>
                      {item.criticalGaps > 0 && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" data-id="element-6248">
                          {item.criticalGaps} critical
                        </span>}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 flex" data-id="element-6249">
                    <div className="h-2 rounded-l-full bg-green-500" style={{
                  width: `${item.percentage}%`
                }} data-id="element-6250"></div>
                    <div className="h-2 rounded-r-full bg-red-500" style={{
                  width: `${100 - item.percentage}%`
                }} data-id="element-6251"></div>
                  </div>
                </div>)}
            </div>
            <button onClick={() => navigate('/security/compliance')} className="mt-4 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300" data-id="element-6252">
              View detailed compliance report →
            </button>
          </div>
          {/* Control Status Distribution */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6" data-id="element-6253">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-6254">
              Control Status Distribution
            </h2>
            <div className="h-64" data-id="element-6255">
              <ResponsiveContainer width="100%" height="100%" data-id="element-6256">
                <PieChart data-id="element-6257">
                  <Pie data={controlStatusData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
                  cx,
                  cy,
                  midAngle,
                  innerRadius,
                  outerRadius,
                  percent,
                  index
                }) => {
                  const RADIAN = Math.PI / 180;
                  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                  const x = cx + radius * Math.cos(-midAngle * RADIAN) * 1.07;
                  const y = cy + radius * Math.sin(-midAngle * RADIAN) * 1.07;
                  return <text x={x} y={y} fill={COLORS[index % COLORS.length]} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" className="text-xs" data-id="element-6259">
                          {`${controlStatusData[index].name} ${controlStatusData[index].percentage}%`}
                        </text>;
                }} data-id="element-6258">
                    {controlStatusData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-6260" />)}
                  </Pie>
                  <Tooltip formatter={value => [`${value} controls`, 'Count']} data-id="element-6261" />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center mt-2 space-x-4" data-id="element-6262">
              {controlStatusData.map((entry, index) => <div key={index} className="flex items-center" data-id="element-6263">
                  <div className="w-3 h-3 rounded-full mr-1" style={{
                backgroundColor: COLORS[index % COLORS.length]
              }} data-id="element-6264"></div>
                  <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-6265">
                    {entry.name}: {entry.value}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default SecurityPostureDashboard;